import { routePaths } from "../../../app/navigation";
import type { SetupChecklist, SetupStep } from "../../../api/schemas";

/** Key of a step of the setup assistant (F-72); also the last segment of its address. */
export type SetupWizardStepKey =
  "establishment" | "work-time-models" | "information" | "employees" | "leave" | "invitations";

/** A step of the setup assistant with the items of the server checklist it covers. */
export interface SetupWizardStep {
  /** Key and address segment of the step. */
  readonly key: SetupWizardStepKey;
  /** Items of the server checklist (T-133) the step covers, in the order shown. */
  readonly items: readonly SetupStep[];
}

/**
 * The steps of the setup assistant in their suggested order (F-72, ADR-0197). Each item of the
 * server checklist belongs to exactly one step. The works council and the information of the
 * employees come before the employees are entered and invited, the leave balances after the
 * employees (they name personnel numbers), and the invitations last, so that the first clock
 * event finds a complete setup. The order is a suggestion only: every step can be opened at any
 * time.
 */
export const setupWizardSteps: readonly SetupWizardStep[] = [
  { key: "establishment", items: ["Establishment"] },
  { key: "work-time-models", items: ["WorkTimeModels"] },
  { key: "information", items: ["WorksCouncil", "EmployeeInformation"] },
  { key: "employees", items: ["Employees"] },
  { key: "leave", items: ["LeavePolicy"] },
  { key: "invitations", items: ["Invitations", "FirstClockEvent"] },
];

/**
 * State of a step of the assistant, derived from the server checklist; `unreported` when the
 * server reports none of its items (an older server, F-85).
 */
export type SetupWizardStepState = "done" | "started" | "open" | "unreported";

/** A step of the assistant with its state. */
export interface SetupWizardStepStatus {
  /** The step. */
  readonly step: SetupWizardStep;
  /** Position of the step, starting at 1. */
  readonly number: number;
  /**
   * Done when every item the server reports for it is done, started when some are, unreported
   * when the server reports none of its items.
   */
  readonly state: SetupWizardStepState;
}

/** Progress of the setup assistant. */
export interface SetupWizardProgress {
  /** The steps with their states, in order. */
  readonly steps: readonly SetupWizardStepStatus[];
  /** Number of done steps. */
  readonly done: number;
  /** Number of counted steps: those with at least one item reported by the server (F-85). */
  readonly total: number;
  /** Whether at least one step is counted and every counted step is done. */
  readonly complete: boolean;
  /**
   * The first counted step that is not done; the first step while no step is counted; `null`
   * once the setup is complete.
   */
  readonly nextOpen: SetupWizardStep | null;
}

/**
 * State of one step: items the server does not report (an older server) are left out; a step
 * without any reported item is `unreported` and neither counts as done nor as open (F-85,
 * ADR-0209).
 *
 * @param step - The step.
 * @param checklist - The checklist of the server.
 * @returns The state.
 */
function stepState(step: SetupWizardStep, checklist: SetupChecklist): SetupWizardStepState {
  const reported = checklist.filter((item) => step.items.includes(item.step));
  const doneItems = reported.filter((item) => item.isDone).length;
  let state: SetupWizardStepState = "open";

  if (reported.length === 0) {
    state = "unreported";
  } else if (doneItems === reported.length) {
    state = "done";
  } else if (doneItems > 0) {
    state = "started";
  }

  return state;
}

/**
 * Derives the progress of the assistant from the server checklist (`useSetupChecklist`); the
 * assistant keeps no state of its own. The overview and the banner of the start screen count only
 * the steps the server reports items for, so a step an older server knows nothing about never
 * keeps the setup open (F-85, ADR-0209).
 *
 * @param checklist - The checklist of the server.
 * @returns The progress.
 */
export function setupWizardProgress(checklist: SetupChecklist): SetupWizardProgress {
  const steps = setupWizardSteps.map((step, index) => {
    const status: SetupWizardStepStatus = {
      step,
      number: index + 1,
      state: stepState(step, checklist),
    };

    return status;
  });
  const counted = steps.filter((status) => status.state !== "unreported");
  const done = counted.filter((status) => status.state === "done").length;
  const total = counted.length;
  const complete = total > 0 && done === total;
  const firstOpen = counted.find((status) => status.state !== "done")?.step ?? null;
  const nextOpen = total === 0 ? (setupWizardSteps[0] ?? null) : firstOpen;
  const progress: SetupWizardProgress = { steps, done, total, complete, nextOpen };

  return progress;
}

/**
 * Finds a step by the last segment of its address.
 *
 * @param key - The segment, for example `leave`.
 * @returns The step with its position, or `undefined` for an unknown segment.
 */
export function findSetupWizardStep(
  key: string | undefined,
): { readonly step: SetupWizardStep; readonly number: number } | undefined {
  const index = setupWizardSteps.findIndex((step) => step.key === key);
  const step = setupWizardSteps[index];
  const found = step === undefined ? undefined : { step, number: index + 1 };

  return found;
}

/**
 * The step before and after a step, `null` at either end.
 *
 * @param key - The key of the step.
 * @returns The neighbouring steps.
 */
export function setupWizardNeighbours(key: SetupWizardStepKey): {
  readonly previous: SetupWizardStep | null;
  readonly next: SetupWizardStep | null;
} {
  const index = setupWizardSteps.findIndex((step) => step.key === key);
  const neighbours = {
    previous: setupWizardSteps[index - 1] ?? null,
    next: setupWizardSteps[index + 1] ?? null,
  };

  return neighbours;
}

/**
 * Address of the page of a step.
 *
 * @param step - The step.
 * @returns The path, for example `/setup/leave`.
 */
export function setupWizardStepPath(step: SetupWizardStep): string {
  const path = `${routePaths.setup}/${step.key}`;

  return path;
}
